import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Indicators,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'ui'
import { toast } from 'react-hot-toast'
import { useCreateDistrictMutation, useCities } from '../queries'

// Validation schema
const schema = yup.object({
  district: yup.string().required('District name cannot be blank'),
  city: yup.string().required('County must be selected'),
})

export type NewDistrictFormValues = yup.InferType<typeof schema>

interface NewDistrictFormProps {
  onSuccess?: (data: any) => void
  onError?: (error: Error) => void
}

export const NewDistrictForm: React.FC<NewDistrictFormProps> = ({
  onSuccess,
  onError,
}) => {
  // Fetch cities data
  const { data: cities, isLoading: isLoadingCities } = useCities()

  // Initialize the mutation
  const createDistrictMutation = useCreateDistrictMutation()

  // Initialize the form
  const form = useForm<NewDistrictFormValues>({
    defaultValues: {
      district: '',
      city: '',
    },
    resolver: yupResolver(schema),
    mode: 'onBlur',
  })

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      // Show loading toast
      const toastId = toast.loading('Adding new district...')

      // Call the mutation
      const result = await createDistrictMutation.mutateAsync({
        district: data.district,
        city: data.city,
      })

      // Handle success
      toast.success('District added successfully', { id: toastId })
      form.reset()

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(result)
      }
    } catch (error) {
      // Handle error
      toast.error('Failed to add district', {
        id: toast.loading(''), // Create and immediately dismiss a loading toast
      })

      // Call onError callback if provided
      if (onError && error instanceof Error) {
        onError(error)
      }
    }
  })

  return (
    <div className="flex flex-col w-full mb-6">
      <article className="mb-2">
        <h2 className="text-xl font-semibold">Add a New District</h2>
      </article>

      <Form {...form}>
        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          <FormField
            control={form.control}
            name="district"
            render={({ field }) => (
              <FormItem>
                <FormLabel>District Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter district name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>County</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={isLoadingCities}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a county" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {cities?.map((city) => (
                      <SelectItem key={city.id} value={city.name}>
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center pt-2">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting || isLoadingCities}
              className="w-full sm:w-auto"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner size="sm" className="mr-2" />
              ) : null}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
